export type RunningPeriod = '28d' | '42d' | '90d' | '180d' | '365d' | 'all';
export type RunningDomain = 'sprint' | 'vo2' | 'threshold' | 'endurance';
export type RunningPhenotype = 'SPRINTER' | 'SPEED' | 'BALANCED' | 'ENDURANCE' | 'DIESEL';
export type RunningView = 'world' | 'benchmark' | 'speed' | 'pace';

export interface RunningEffort {
  duration_sec: number;
  speed_kmh: number;
  pace_sec_per_km: number;
  distance_m: number;
  activity_id: number;
  activity_date: string;
  start_offset_sec: number;
  end_offset_sec: number;
  quality_score: number;
  confidence: number;
  quality_flags: string[];
  grade_avg: number | null;
  surface_type: string;
  source: string;
  valid_attempts: number;
  vdot_equivalent: number | null;
  vdot_reliability: 'secondary' | 'equivalent' | null;
}
export interface RunningBenchmark {
  percentile: number;
  class: string;
  sample_size: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  version: string;
  robust_z_score: number | null;
  cohort: { key: string; sex: string | null; age_min: number | null; age_max: number | null; scope: 'age' | 'broad' | 'sex' | 'global' };
  effort: RunningEffort;
}
export interface RunningCompetitionReference {
  key: 'asian_bronze' | 'world_entry' | 'olympic_entry';
  edition: string;
  basis: 'bronze_medal' | 'entry_standard';
  version: string;
  source_url: string;
  status: 'available' | 'outside_reference_range';
  is_competition_qualification: false;
  reference_score: number | null;
  reference_speed_kmh: number | null;
  reference_pace_sec_per_km: number | null;
  gap_percentage_points: number | null;
  meets_reference: boolean | null;
  interpolated: boolean | null;
  anchors: { event: string; distance_m: number; time_sec: number }[];
}
export interface RunningWorldBenchmark {
  score: number;
  version: string;
  sex: 'male' | 'female';
  reference_speed_kmh: number;
  reference_pace_sec_per_km: number;
  reference_distance_m: number;
  interpolated: boolean;
  anchors: { event: string; distance_m: number; time_sec: number; record_date: string; source_url: string; venue_type?: string }[];
  above_reference: boolean;
  rolling_start: boolean;
  competition_references?: RunningCompetitionReference[];
  effort: RunningEffort;
}
export interface RunningWorldSummary {
  version: string;
  competition_reference_version?: string;
  reviewed_at: string;
  source: string;
  source_url: string;
  method: string;
  sex: 'male' | 'female' | null;
  age_adjusted: false;
  is_population_percentile: false;
  status: 'available' | 'missing_sex' | 'quality_limited' | 'no_effort';
  domain_scores: Record<RunningDomain, number | null>;
  domain_coverage: Record<RunningDomain, number>;
  overall_score: number | null;
  phenotype: RunningPhenotype | null;
  strength: RunningDomain | null;
  opportunity: RunningDomain | null;
}
export interface RunningProfilePoint {
  duration_sec: number;
  observed: RunningEffort | null;
  benchmark: RunningBenchmark | null;
  world_benchmark?: RunningWorldBenchmark | null;
  benchmark_status: 'no_effort' | 'quality_limited' | 'eligible';
  all_time: RunningEffort | null;
  all_time_benchmark: RunningBenchmark | null;
  all_time_world_benchmark?: RunningWorldBenchmark | null;
  previous: RunningEffort | null;
  previous_benchmark: RunningBenchmark | null;
  previous_world_benchmark?: RunningWorldBenchmark | null;
  previous_28d: RunningEffort | null;
  previous_90d: RunningEffort | null;
  change_percent: number | null;
}
export interface RunningProfile {
  athlete_id: number;
  period: RunningPeriod | 'custom';
  start_date: string | null;
  end_date: string;
  comparison_start_date: string | null;
  comparison_end_date: string | null;
  status: 'ready' | 'pending' | 'empty';
  processing: { pending: number; ready: number; excluded: number; reasons: { reason: string; count: number }[] };
  benchmark: { version: string | null; reference_date: string | null; source: string | null; sex: string | null; age: number | null; minimum_population: number; status: 'available' | 'insufficient_data' | 'quality_limited' };
  world_benchmark?: RunningWorldSummary;
  classes: { key: string; min: number; max: number; exclusive_min?: boolean }[];
  profile: RunningProfilePoint[];
  domain_scores: Record<RunningDomain, number | null>;
  domain_coverage: Record<RunningDomain, number>;
  overall_score: number | null;
  phenotype: RunningPhenotype | null;
  strength: RunningDomain | null;
  opportunity: RunningDomain | null;
  grade_adjusted: false;
  maximal_effort_verified: false;
  snapshots: { date: string; benchmark_version: string | null; domain_scores: Record<RunningDomain, number | null> }[];
}

export function formatRunningPace(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return '—';
  const rounded = Math.round(seconds);
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`;
}

export function formatRunningRaceTime(seconds: number): string {
  const centiseconds = Math.round(seconds * 100);
  const minutes = Math.floor(centiseconds / 6000);
  const remainder = ((centiseconds % 6000) / 100).toFixed(2);
  return minutes ? `${minutes}:${remainder.padStart(5, '0')}` : remainder;
}

export function runningPlotValue(point: RunningProfilePoint, view: RunningView, historic = false): number | null {
  const value = view === 'world' ? (historic ? point.all_time_world_benchmark : point.world_benchmark)?.score : view === 'benchmark'
    ? (historic ? point.all_time_benchmark : point.benchmark)?.percentile
    : (historic ? point.all_time : point.observed)?.speed_kmh;
  return value != null && Number.isFinite(value) ? value : null;
}

export function runningEffortForView(point: RunningProfilePoint | undefined, view: RunningView): RunningEffort | null {
  return (view === 'world' ? point?.world_benchmark?.effort : view === 'benchmark' ? point?.benchmark?.effort : point?.observed) ?? null;
}

export function isRunningProfile(data: unknown): data is RunningProfile {
  return !!data && typeof data === 'object' && Array.isArray((data as RunningProfile).profile)
    && Array.isArray((data as RunningProfile).classes) && !!(data as RunningProfile).processing
    && !!(data as RunningProfile).domain_scores && !!(data as RunningProfile).domain_coverage
    && typeof (data as RunningProfile).athlete_id === 'number' && !!(data as RunningProfile).benchmark;
}
